/**
 * Event Logs Page
 * 
 * Main page for viewing event logs with features:
 * - Event log list with pagination
 * - Filtering and search capabilities
 * - Statistics dashboard
 * - Detail view modal
 * 
 * Read-only interface for event log visualization
 */

import { FC, useCallback, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { eventLogService } from '@/services';
import StandardPage from '@/components/Common/StandardPage';
import DataTable from '@/components/DataTable/DataTable';
import { createEventLogColumns } from '@/components/DataTable/eventLogColumns';
import Modal from '@/components/Common/Modal';
import type { EventLogListItem, EventLogFilters } from '@/types';
import styles from './EventLogsPage.module.css';

const EventLogsPage: FC = () => {
  const [selectedEventLog, setSelectedEventLog] = useState<EventLogListItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [filters] = useState<EventLogFilters>({});

  /**
   * Fetch event logs with react-query
   */
  const {
    data: eventLogs = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['event-logs', filters],
    queryFn: () => eventLogService.getEventLogs(filters),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  /**
   * Fetch event log statistics
   */
  const {
    data: statistics,
    isLoading: isLoadingStats,
  } = useQuery({
    queryKey: ['event-logs-statistics'],
    queryFn: () => eventLogService.getEventLogStatistics(),
    refetchInterval: 60000, // Refresh every minute
  });

  /**
   * Handle view event log detail
   */
  const handleView = useCallback((eventLog: EventLogListItem) => {
    setSelectedEventLog(eventLog);
    setIsDetailModalOpen(true);
  }, []);

  /**
   * Handle close detail modal
   */
  const handleCloseDetail = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedEventLog(null);
  }, []);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  /**
   * Create columns with actions memoized
   */
  const columns = useMemo(
    () => createEventLogColumns({ onView: handleView }),
    [handleView]
  );

  /**
   * Format error message for display
   */
  const getErrorMessage = (): Error => {
    if (error instanceof Error) {
      return error;
    }
    return new Error('Erro ao carregar logs de eventos');
  };

  return (
    <>
      <StandardPage
        header={{
          title: 'Logs de Eventos',
          subtitle: 'Visualize e monitore os logs de processamento de eventos',
          actions: [
            {
              label: 'Atualizar',
              variant: 'secondary',
              onClick: handleRefresh,
              disabled: isLoading,
            },
          ],
        }}
        isLoading={isLoading}
        isError={isError}
        error={getErrorMessage()}
        onRefetch={refetch}
      >
        {/* Statistics Cards */}
        {statistics && !isLoadingStats && (
          <div className={styles.statisticsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{statistics.total}</div>
              <div className={styles.statLabel}>Total de Logs</div>
            </div>
            <div className={styles.statCard}>
              <div className={`${styles.statValue} ${styles.success}`}>
                {statistics.successCount}
              </div>
              <div className={styles.statLabel}>Sucessos</div>
            </div>
            <div className={styles.statCard}>
              <div className={`${styles.statValue} ${styles.error}`}>
                {statistics.errorCount}
              </div>
              <div className={styles.statLabel}>Erros</div>
            </div>
            <div className={styles.statCard}>
              <div className={`${styles.statValue} ${styles.info}`}>
                {statistics.successRate.toFixed(1)}%
              </div>
              <div className={styles.statLabel}>Taxa de Sucesso</div>
            </div>
          </div>
        )}

        {/* Event Logs Table */}
        <div className={styles.tableContainer}>
          <DataTable
            data={eventLogs}
            columns={columns}
            isLoading={false}
            enableSorting={true}
            enablePagination={true}
            emptyMessage="Nenhum log de evento encontrado"
            showSkeleton={true}
          />
        </div>
      </StandardPage>

      {/* Event Log Detail Modal */}
      <Modal
        open={isDetailModalOpen}
        onClose={handleCloseDetail}
        title="Detalhes do Log de Evento"
        description="Informações completas sobre o processamento do evento"
        size="lg"
      >
        {selectedEventLog && (
          <div className={styles.detailContent}>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <label>Log ID</label>
                <span>#{selectedEventLog.id}</span>
              </div>
              <div className={styles.detailItem}>
                <label>Event ID</label>
                <span>#{selectedEventLog.agentEventId}</span>
              </div>
              <div className={styles.detailItem}>
                <label>Status</label>
                <span className={`${styles.statusBadge} ${styles[selectedEventLog.status.toLowerCase()]}`}>
                  {selectedEventLog.status}
                </span>
              </div>
              <div className={styles.detailItem}>
                <label>Tentativas</label>
                <span>{selectedEventLog.retries}</span>
              </div>
              <div className={styles.detailItem}>
                <label>Processado em</label>
                <span>{new Date(selectedEventLog.datProcess).toLocaleString('pt-BR')}</span>
              </div>
              <div className={styles.detailItem}>
                <label>Criado em</label>
                <span>{new Date(selectedEventLog.createdAt).toLocaleString('pt-BR')}</span>
              </div>
              {selectedEventLog.updatedAt && (
                <div className={styles.detailItem}>
                  <label>Atualizado em</label>
                  <span>{new Date(selectedEventLog.updatedAt).toLocaleString('pt-BR')}</span>
                </div>
              )}
            </div>

            {/* Error Message Section */}
            {selectedEventLog.errorMessage && (
              <div className={styles.errorSection}>
                <label>Mensagem de Erro</label>
                <div className={styles.errorContent}>
                  {selectedEventLog.errorMessage}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default EventLogsPage;